import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import type { Country } from '@/modules/countries/utils/countriesData'
import { useHomePopularDestinations } from '@/modules/plans/hooks/useHomePopularDestinations'
import { PopularDestinationCountryCard } from '@/modules/plans/components/PopularDestinationCountryCard'
import { RegionalCountryCountFilter } from '@/modules/plans/components/RegionalCountryCountFilter'
import { RegionalCoverageCountryCard } from '@/modules/plans/components/RegionalCoverageCountryCard'
import {
  findRegionalRegion,
  getRegionalCatalogRegions,
  buildRegionalCountryCountVariants,
  buildRegionalCheckoutPlan,
} from '@/modules/plans/utils/regionalCatalog.utils'
import { countryPathName } from '@/modules/plans/utils/countryPathName'
import type { FAQ, BlogPost } from '@/shared/types'
import { getCountryFlag } from '@/shared/utils/countryFlags'
import { useNewsletterSubscribe } from '@/modules/marketing/hooks/useNewsletterSubscribe'
import { useFetchWhenNearViewport } from '@/shared/hooks/useFetchWhenNearViewport'
import { OptimizedPicture } from '@/shared/components/OptimizedPicture'
import { IMAGE_SPECS, specForCityImage, hideHeroLcpPrerender } from '@/shared/utils/imageAssets'

/** FAQs shown on the home page; full list lives on /faq */
const HOME_FAQ_PREVIEW_LIMIT = 8

const Home = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'local' | 'regional' | 'global'>('local')
  const [testimonialIndex, setTestimonialIndex] = useState(0)
  const [top10StartIndex, setTop10StartIndex] = useState(0)
  const {
    email,
    setEmail,
    onSubmit: onNewsletterSubmit,
    busy: newsletterBusy,
    info: newsletterInfo,
    err: newsletterErr,
  } = useNewsletterSubscribe('home')
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)
  const [faqs, setFaqs] = useState<FAQ[]>([])
  const [faqsLoading, setFaqsLoading] = useState(false)
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([])
  const [blogLoading, setBlogLoading] = useState(false)
  const popularDestinationsRef = useRef<HTMLElement>(null)
  const faqSectionRef = useRef<HTMLElement>(null)
  const blogSectionRef = useRef<HTMLElement>(null)
  const [heroSearchQuery, setHeroSearchQuery] = useState('')
  const [destinationsSearchQuery, setDestinationsSearchQuery] = useState('')
  const [searchCountries, setSearchCountries] = useState<Country[] | null>(null)
  const searchCountriesLoadingRef = useRef(false)
  // const scrollContainerRef = useRef<HTMLDivElement>(null) // Unused
  // const journeyScrollRef = useRef<HTMLDivElement>(null) // Unused
  const location = useLocation()
  const searchResultsRef = useRef<HTMLDivElement>(null)
  const heroSearchInputRef = useRef<HTMLInputElement>(null)
  const destinationsSearchResultsRef = useRef<HTMLDivElement>(null)
  // const destinationsSearchInputRef = useRef<HTMLInputElement>(null) // Unused

  // Automatically scroll to the popular destinations section when navigating from other pages using hash
  useEffect(() => {
    if (location.hash === '#popular-destinations') {
      setTimeout(() => {
        const element = document.getElementById('popular-destinations')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [location.hash])

  // Close search dropdown when clicking outside (hero search)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const searchContainer = target.closest('.hero-search-container')
      const searchResults = searchResultsRef.current
      
      // Don't close if clicking inside search container or results dropdown
      if (!searchContainer && !(searchResults && searchResults.contains(target)) && heroSearchQuery.trim()) {
        setHeroSearchQuery('')
      }
    }

    if (heroSearchQuery.trim()) {
      // Use a small delay to allow click events on dropdown items to fire first
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [heroSearchQuery])

  // Close search dropdown when clicking outside (destinations search)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const searchContainer = target.closest('.destinations-search-container')
      const searchResults = destinationsSearchResultsRef.current
      
      // Don't close if clicking inside search container or results dropdown
      if (!searchContainer && !(searchResults && searchResults.contains(target)) && destinationsSearchQuery.trim()) {
        setDestinationsSearchQuery('')
      }
    }

    if (destinationsSearchQuery.trim()) {
      // Use a small delay to allow click events on dropdown items to fire first
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [destinationsSearchQuery])
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null)
  const [selectedRegionalCountryCount, setSelectedRegionalCountryCount] = useState<number | null>(
    null
  )
  // Top 10 Destinations - Hardcoded popular destinations with local images
  const [destinations] = useState<Array<{ name: string; price: string; image: string }>>([
    {
      name: 'United Kingdom',
      price: '1.36',
      image: '/IMAGES/Cities/London.jpg'
    },
    {
      name: 'France',
      price: '1.25',
      image: '/IMAGES/Cities/Paris.jpg'
    },
    {
      name: 'Italy',
      price: '1.18',
      image: '/IMAGES/Cities/Rome.jpg'
    },
    {
      name: 'Spain',
      price: '1.22',
      image: '/IMAGES/Cities/Barcelona.jpg'
    },
    {
      name: 'United Arab Emirates',
      price: '1.45',
      image: '/IMAGES/Cities/Dubai.jpg'
    },
    {
      name: 'Singapore',
      price: '1.50',
      image: '/IMAGES/Cities/Singapore.jpg'
    },
    {
      name: 'Australia',
      price: '1.40',
      image: '/IMAGES/Cities/Sydney.jpg'
    },
    {
      name: 'United States',
      price: '1.55',
      image: '/IMAGES/Cities/NewYork.jpg'
    },
    {
      name: 'Netherlands',
      price: '1.30',
      image: '/IMAGES/Cities/Amsterdam.jpg'
    },
    {
      name: 'Thailand',
      price: '1.15',
      image: '/IMAGES/Cities/Thailand.jpg'
    }
  ])
  const [destinationsLoading] = useState(false)

  // Auto-rotate Top 10 Destinations - Show 4 cards at a time
  useEffect(() => {
    if (destinationsLoading || destinations.length === 0) return

    const rotateInterval = setInterval(() => {
      setTop10StartIndex((prevIndex) => {
        // Move to next set of 4 cards, loop back to start when reaching the end
        const nextIndex = prevIndex + 4
        if (nextIndex >= destinations.length) {
          return 0 // Loop back to beginning
        }
        return nextIndex
      })
    }, 3000) // Change every 3 seconds

    return () => {
      clearInterval(rotateInterval)
    }
  }, [destinationsLoading, destinations.length])

  const {
    data: homePopular,
    loading: homePopularLoading,
    error: homePopularError,
    refetch: refetchHomePopular,
  } = useHomePopularDestinations({ deferUntilRef: popularDestinationsRef })

  const localDestinationsForGrid = useMemo(() => {
    if (!homePopular?.local?.length) return []
    return homePopular.local.map(d => ({
      name: d.name,
      iso: d.iso,
      flag: getCountryFlag(d.iso),
      priceStr:
        d.available && d.startingFromUsd != null ? d.startingFromUsd.toFixed(2) : '0',
      available: d.available,
    }))
  }, [homePopular])

  const regionalCatalogRegions = useMemo(
    () => getRegionalCatalogRegions(homePopular?.regional),
    [homePopular]
  )

  const selectedRegionalRegion = useMemo(
    () => findRegionalRegion(homePopular?.regional, selectedRegion),
    [homePopular, selectedRegion]
  )

  const regionalCountryCountVariants = useMemo(
    () => buildRegionalCountryCountVariants(selectedRegionalRegion),
    [selectedRegionalRegion]
  )

  const activeRegionalVariant = useMemo(() => {
    if (regionalCountryCountVariants.length === 0) return null
    return (
      regionalCountryCountVariants.find(v => v.countryCount === selectedRegionalCountryCount) ??
      regionalCountryCountVariants[0]
    )
  }, [regionalCountryCountVariants, selectedRegionalCountryCount])

  const regionalBundlesForView = activeRegionalVariant?.bundles ?? []
  const regionalCountriesForView = activeRegionalVariant?.countries ?? []

  useEffect(() => {
    if (regionalCountryCountVariants.length === 0) {
      setSelectedRegionalCountryCount(null)
      return
    }
    setSelectedRegionalCountryCount(regionalCountryCountVariants[0].countryCount)
  }, [selectedRegion, regionalCountryCountVariants])

  const homeGlobalPlans = useMemo(
    () => homePopular?.global ?? [],
    [homePopular]
  )

  const fetchBlogPreview = useCallback(async () => {
    try {
      setBlogLoading(true)
      // Dynamic import keeps blog.service off the Home critical request chain.
      const { blogService } = await import('@/modules/blog/services/blog.service')
      const response = await blogService.getPosts({ limit: 3 })
      setBlogPosts(response.posts || [])
    } catch (err) {
      console.error('Error fetching blog preview:', err)
      setBlogPosts([])
    } finally {
      setBlogLoading(false)
    }
  }, [])

  const fetchFaqs = useCallback(async () => {
    try {
      setFaqsLoading(true)
      // Dynamic import keeps faq.service off the Home critical request chain.
      const { faqService } = await import('@/modules/faq/services/faq.service')
      const data = await faqService.getFaqs()
      setFaqs(data || [])
    } catch (error) {
      console.error('Error fetching FAQs:', error)
      setFaqs([])
    } finally {
      setFaqsLoading(false)
    }
  }, [])

  useFetchWhenNearViewport(fetchBlogPreview, blogSectionRef)
  useFetchWhenNearViewport(fetchFaqs, faqSectionRef)

  const loadSearchCountries = useCallback(() => {
    if (searchCountries || searchCountriesLoadingRef.current) return
    searchCountriesLoadingRef.current = true
    void import('@/modules/countries/utils/countriesData').then((mod) => {
      setSearchCountries(mod.allCountries)
    })
  }, [searchCountries])

  // All countries for search (including Coming Soon)
  const allCountriesForSearch = useMemo(() => {
    if (!searchCountries) return []
    return searchCountries.map(country => ({
      name: country.name,
      flag: country.flag,
      price: country.prices?.['1GB']?.toFixed(2) || 'N/A', // Price per GB from 1GB plan, or N/A if no prices
      country: country, // Keep full country object for navigation
      status: country.status,
    }))
  }, [searchCountries])

  // Filter countries for hero search (includes all countries)
  const heroSearchResults = useMemo(() => {
    if (!heroSearchQuery.trim()) {
      return []
    }
    const query = heroSearchQuery.toLowerCase()
    return allCountriesForSearch
      .filter(dest => 
        dest.name.toLowerCase().includes(query) ||
        dest.country.region?.toLowerCase().includes(query)
      )
      .slice(0, 10) // Show up to 10 results in dropdown
  }, [heroSearchQuery, allCountriesForSearch])

  // Filter countries for destinations search (includes all countries)
  // const destinationsSearchResults = useMemo(() => { // Unused
  //   if (!destinationsSearchQuery.trim()) {
  //     return []
  //   }
  //   const query = destinationsSearchQuery.toLowerCase()
  //   return allCountriesForSearch
  //     .filter(dest => 
  //       dest.name.toLowerCase().includes(query) ||
  //       dest.country.region?.toLowerCase().includes(query)
  //     )
  //     .slice(0, 10) // Show up to 10 results in dropdown
  // }, [destinationsSearchQuery, allCountriesForSearch])

  // Handle country click from search
  const handleCountryClick = (countryName: string) => {
    navigate(`/country/${encodeURIComponent(countryName)}`)
    setHeroSearchQuery('') // Clear search after navigation
  }

  // Handle country click from destinations search
  // const handleDestinationsCountryClick = (countryName: string) => { // Unused
  //   navigate(`/country/${encodeURIComponent(countryName)}`)
  //   setDestinationsSearchQuery('') // Clear search after navigation
  // }

  // Handle destination card click - navigate to country packages page to select package
  // Note: destination.name is now the country name directly from API (e.g., "Austria", not "Vienna, Austria")
  const handleDestinationClick = (destination: { name: string; price: string }) => {
    // Since we're using country names directly from API, use the name as-is
    // But handle cases where it might still be in "City, Country" format (backward compatibility)
    let countryName = destination.name
    
    // If name contains a comma, extract country name (e.g., "Vienna, Austria" -> "Austria")
    if (countryName.includes(', ')) {
      countryName = countryName.split(', ').pop() || countryName
    }
    
    // Map common abbreviations to full country names
    const countryNameMap: Record<string, string> = {
      'UK': 'United Kingdom',
      'USA': 'United States',
      'UAE': 'United Arab Emirates',
    }
    
    // Replace abbreviation with full name if it exists
    countryName = countryNameMap[countryName] || countryName
    
    // Navigate to the country packages page where users can browse and select their preferred eSIM plan
    navigate(`/country/${encodeURIComponent(countryName)}`)
  }

  // const handleScroll = (direction: 'left' | 'right', ref?: React.RefObject<HTMLDivElement>) => { // Unused
  //   const container = ref?.current || scrollContainerRef.current
  //   if (container) {
  //     const scrollAmount = 400
  //     container.scrollBy({
  //       left: direction === 'right' ? scrollAmount : -scrollAmount,
  //       behavior: 'smooth'
  //     })
  //   }
  // }

  const testimonials = [
    {
      name: 'Mike taylor',
      location: 'Malaysia',
      quote: 'I bought a 3GB eSIM valid for 30 days, used it for 13 days in London, and the service was excellent. I highly recommend it — activation is simple and it\'s ready to use instantly, no hassle.',
      image: '/IMAGES/4.jpg',
    },
    {
      name: 'Chris Thomas',
      location: 'CEO of Red Button',
      quote: 'TTelGo eSIM has transformed how we handle business travel. The activation process is seamless, and the coverage is exceptional across all our destinations.',
      image: '/IMAGES/4.jpg',
    },
    {
      name: 'Sarah Johnson',
      location: 'Travel Blogger',
      quote: 'As a frequent traveler, TTelGo eSIM has been a game-changer. No more hunting for SIM cards at airports. Just scan, activate, and you\'re connected instantly.',
      image: '/IMAGES/4.jpg',
    },
  ]


  return (
    <div className="w-full">
      {/* Hero Section - Merged with Navbar */}
      <section 
        className="relative bg-white z-30 mt-[0.6rem] md:mt-4 pt-4 pb-8 md:pt-12 md:pb-8"
      >
        {/* Content Container with proper z-index */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pb-2 pt-0">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-16 xl:gap-20">
            {/* Left Content */}
            <div
              className="flex-1 relative z-20 w-full text-center md:text-left"
            >
              {/* Top pill — matches hero layout in design */}
              <div
                className="mb-5 flex justify-center md:justify-start"
                >
                <span
                  className="inline-block rounded-full px-4 py-2 text-xs md:text-sm lg:text-base shadow-md"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 500,
                    backgroundColor: '#CC0000',
                    color: '#ffffff',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06)',
                  }}
                >
                  Get started with affordable travel data plans.
                </span>
              </div>

              {/* Main Headline */}
              <h1
                className="mb-4 text-xl md:text-3xl lg:text-[42px] leading-tight"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  color: '#181E4B',
                  lineHeight: '1.3',
                }}
              >
                <span className="block mb-1">Stay connected</span>
                <span className="block mb-1">
                  <span style={{ color: '#181E4B' }}>in over </span>
                  <span
                    style={{
                      color: '#CC0000',
                      textDecoration: 'underline',
                      textDecorationColor: '#CC0000',
                      textUnderlineOffset: '0.12em',
                    }}
                  >
                    200+
                  </span>
                </span>
                <span
                  className="block mb-1"
                  style={{
                    color: '#CC0000',
                    textDecoration: 'underline',
                    textDecorationColor: '#CC0000',
                    textUnderlineOffset: '0.12em',
                  }}
                >
                  destinations
                </span>
                <span className="block" style={{ color: '#181E4B' }}>
                  worldwide
                </span>
              </h1>
              <p
                className="mb-6 max-w-xl mx-auto md:mx-0 text-base md:text-lg lg:text-[20px] leading-relaxed"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  color: '#5E6282',
                }}
              >
                Activate in minutes, no physical SIM, no queues. Reliable coverage in all destinations for travellers, students and business users.
              </p>
              
              {/* View All Plans Button */}
              <button
                onClick={() => {
                  const element = document.getElementById('popular-destinations')
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                }}
                className="mb-8 inline-flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 bg-gradient-to-r from-telgo-red to-red-600 text-white text-sm md:text-base font-semibold rounded-xl md:rounded-2xl hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl mx-auto md:mx-0"
                style={{ transformOrigin: 'center' }}
              >
                <span className="flex items-center justify-center gap-2">
                View All Plans
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                </span>
              </button>
              
              {/* Search Bar */}
              <div className="relative w-full max-w-md min-w-0 hero-search-container mx-auto md:mx-0" style={{ zIndex: 9999 }}>
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  ref={heroSearchInputRef}
                  type="text"
                  value={heroSearchQuery}
                  onFocus={loadSearchCountries}
                  onChange={(e) => {
                    loadSearchCountries()
                    setHeroSearchQuery(e.target.value)
                  }}
                  placeholder="Search for your next destination"
                  className="w-full min-w-0 pl-12 pr-3 sm:pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-telgo-red focus:border-transparent text-base text-gray-900 bg-white/95 backdrop-blur-sm relative z-0"
                />
                
                {/* Search Results Dropdown - Positioned absolutely relative to search container */}
                {heroSearchQuery.trim() && heroSearchResults.length > 0 && (
                  <div
                    ref={searchResultsRef}
                    className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-96 overflow-y-auto"
                    style={{ zIndex: 99999 }}
                  >
                    {heroSearchResults.map((result) => (
                      <div
                        key={result.country.id}
                        onClick={() => handleCountryClick(result.name)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        <div className="text-2xl">{result.flag}</div>
                        <div className="flex-1">
                          <div className="text-gray-900 font-medium">{result.name}</div>
                          {result.status === 'Coming Soon' || result.price === 'N/A' || result.price === '0.00' ? (
                            <div className="text-sm text-orange-600 font-medium">Coming Soon</div>
                          ) : (
                            <div className="text-sm text-gray-500">From ${result.price}/GB</div>
                          )}
                        </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* No Results Message - Positioned absolutely relative to search container */}
                {heroSearchQuery.trim() && heroSearchResults.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-2xl p-4" style={{ zIndex: 99999 }}>
                    <p className="text-gray-500 text-center">No countries found. Try a different search.</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Image — no entrance animation; keeps LCP visible immediately */}
            <div
              className="hidden lg:flex flex-1 items-center justify-center relative z-20"
              style={{ marginTop: '-1.5rem', paddingLeft: '2rem', paddingRight: '1rem' }}
            >
              <OptimizedPicture
                spec={IMAGE_SPECS.hero}
                alt="Hero illustration"
                className="object-contain"
                style={{
                  width: '100%',
                  maxWidth: '1200px',
                  height: 'auto',
                }}
                loading="eager"
                fetchPriority="high"
                onLoad={hideHeroLcpPrerender}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Your Journey Section */}
      <section className="pt-4 md:pt-8 pb-16 bg-transparent relative z-20 border-t md:border-0 border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            >
            <h2 
              className="mb-4 text-center text-2xl md:text-[35px]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                color: '#181E4B'
              }}
            >
              Top 10 Destinations
            </h2>
            <p 
              className="mb-8 max-w-2xl mx-auto text-center text-base md:text-lg lg:text-[20px]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                color: '#5E6282'
              }}
            >
              Explore our top 10 most popular destinations. Discover the perfect eSIM plan for your next adventure.
            </p>
            
            {/* Destination Cards — 2×2 grid on small screens; single row on md+ */}
            <div className="relative overflow-visible px-0.5 sm:px-0">
              {destinationsLoading ? (
                <div className="grid grid-cols-2 gap-3 pb-4 md:flex md:flex-row md:flex-nowrap md:justify-center md:gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="min-w-0 w-full max-w-full md:w-[280px] md:max-w-[280px] md:flex-shrink-0 bg-white rounded-xl shadow-md overflow-hidden animate-pulse"
                    >
                      <div className="h-32 md:h-40 bg-gray-200"></div>
                      <div className="p-3">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 pb-4 md:flex md:flex-row md:flex-nowrap md:justify-center md:gap-4">
                  {[0, 1, 2, 3].map((offset) => {
                      const actualIndex = (top10StartIndex + offset) % destinations.length
                      const displayDest = destinations[actualIndex]
                      return (
                  <div
                    key={`${displayDest.name}-${actualIndex}`}
                    onClick={() => handleDestinationClick(displayDest)}
                    className="min-w-0 w-full max-w-full md:w-[280px] md:max-w-[280px] md:flex-shrink-0 bg-white rounded-xl shadow-lg md:shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer flex flex-col"
                  >
                    <div className="relative w-full h-32 md:h-40 overflow-hidden bg-gray-100 flex-shrink-0">
                      {displayDest.image ? (
                        <OptimizedPicture
                          spec={specForCityImage(displayDest.image)}
                          sizes="(max-width: 768px) 50vw, 280px"
                          alt={displayDest.name}
                          className="absolute inset-0 h-full w-full object-cover object-center"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                          }}
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            const host = target.closest('picture')?.parentElement ?? target.parentElement
                            if (host && !host.querySelector('[data-city-fallback]')) {
                              const placeholder = document.createElement('div')
                              placeholder.dataset.cityFallback = '1'
                              placeholder.className = 'absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 px-2 text-center text-sm text-gray-600'
                              placeholder.textContent = displayDest.name
                              host.appendChild(placeholder)
                            }
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 px-2 text-center text-sm text-gray-600">
                          {displayDest.name}
                        </div>
                      )}
                    </div>
                    <div className="p-3 md:p-4 flex-shrink-0">
                      <div
                        className="mb-1 truncate text-left text-sm font-semibold md:text-lg"
                        style={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 600,
                          color: '#181E4B'
                        }}
                      >
                        {displayDest.name}
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className="text-xs text-gray-500 md:text-sm"
                          style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500
                          }}
                        >
                          Starting from
                        </span>
                        <span
                          className="whitespace-nowrap text-sm font-bold md:text-lg"
                          style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 600,
                            color: '#CC0000'
                          }}
                        >
                          USD {displayDest.price}/GB
                        </span>
                      </div>
                    </div>
                  </div>
                      )
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Destinations Section */}
      <section
        id="popular-destinations"
        ref={popularDestinationsRef}
        className="pt-0 md:pt-0 pb-16 bg-transparent border-t md:border-0 border-gray-200 -mt-4"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            >
            <h2 
              className="mb-8 text-center text-2xl md:text-[35px]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                color: '#181E4B'
              }}
            >
              Popular Destinations
            </h2>
            
            {/* Tabs */}
            <div className="flex gap-2 mb-8 justify-center">
              <button
                onClick={() => {
                  setActiveTab('local')
                  setSelectedRegion(null)
                }}
                className={`px-3 py-1.5 md:px-6 md:py-2.5 rounded-md md:rounded-lg text-xs md:text-base font-medium transition-colors ${
                  activeTab === 'local'
                    ? 'bg-telgo-red text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Local eSIMs
              </button>
              <button
                onClick={() => {
                  setActiveTab('regional')
                  setSelectedRegion(null)
                }}
                className={`px-3 py-1.5 md:px-6 md:py-2.5 rounded-md md:rounded-lg text-xs md:text-base font-medium transition-colors ${
                  activeTab === 'regional'
                    ? 'bg-telgo-red text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Regional eSIMs
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('global')
                  setSelectedRegion(null)
                }}
                className={`px-3 py-1.5 md:px-6 md:py-2.5 rounded-md md:rounded-lg text-xs md:text-base font-medium transition-colors ${
                  activeTab === 'global'
                    ? 'bg-telgo-red text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Global eSIMs
              </button>
            </div>
            
            {/* Local eSIMs — 4×4 on desktop, prices from precomputed /home-data snapshot */}
            {activeTab === 'local' && (
              <div className="mb-6">
                {homePopularLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-telgo-red"></div>
                    <p className="mt-4 text-gray-600">Loading destinations…</p>
                  </div>
                ) : homePopularError && localDestinationsForGrid.length === 0 ? (
                  <div className="text-center py-12 rounded-xl border border-red-100 bg-red-50/50 px-4">
                    <p className="text-gray-800 mb-4">{homePopularError}</p>
                    <button
                      type="button"
                      onClick={() => void refetchHomePopular()}
                      className="px-6 py-2.5 bg-telgo-red text-white font-semibold rounded-lg hover:opacity-90"
                    >
                      Try again
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
                      {localDestinationsForGrid.map((dest, index) => (
                        <PopularDestinationCountryCard
                          key={dest.iso}
                          variant="grid"
                          gridIndex={index}
                          isoCode={dest.iso}
                          flag={dest.flag}
                          name={dest.name}
                          pricePerGb={dest.priceStr}
                          priceDisplay="perGb"
                          available={dest.available}
                          onClick={() =>
                            navigate(
                              `/country/${encodeURIComponent(countryPathName(dest.name))}`
                            )
                          }
                        />
                      ))}
                    </div>
                    <div className="text-center relative z-10">
                      <button
                        type="button"
                        onClick={() => navigate('/shop')}
                        className="px-6 py-2.5 bg-white border-2 border-telgo-red text-telgo-red font-semibold rounded-lg hover:bg-telgo-red hover:text-white transition-colors cursor-pointer relative z-10"
                      >
                        View All Countries
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Regional eSIMs — prices from /home-data snapshot */}
            {activeTab === 'regional' && (
              <div className="mb-6">
                {homePopularLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-telgo-red"></div>
                    <p className="mt-4 text-gray-600">Loading destinations…</p>
                  </div>
                ) : homePopularError ? (
                  <div className="text-center py-12 rounded-xl border border-red-100 bg-red-50/50 px-4">
                    <p className="text-gray-800 mb-4">{homePopularError}</p>
                    <button
                      type="button"
                      onClick={() => void refetchHomePopular()}
                      className="px-6 py-2.5 bg-telgo-red text-white font-semibold rounded-lg hover:opacity-90"
                    >
                      Try again
                    </button>
                  </div>
                ) : regionalCatalogRegions.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-lg text-gray-500">No regional plans available right now.</p>
                  </div>
                ) : (
                  <>
                    {!selectedRegion ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
                        {regionalCatalogRegions.map(region => (
                          <button
                            key={region.key}
                            type="button"
                            onClick={() => setSelectedRegion(region.key)}
                            className="bg-white rounded-xl shadow-md p-6 text-center cursor-pointer"
                          >
                            <div className="text-gray-900 font-semibold text-lg">{region.label}</div>
                            <div className="text-gray-600 text-sm mt-2">
                              {region.bundleCount}{' '}
                              {region.bundleCount === 1 ? 'bundle' : 'bundles'} ·{' '}
                              {region.countryCount}{' '}
                              {region.countryCount === 1 ? 'country' : 'countries'}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-2xl font-bold text-center md:text-left" style={{ color: '#181E4B' }}>
                            {selectedRegionalRegion?.label ?? selectedRegion}
                          </h3>
                          <button
                            onClick={() => setSelectedRegion(null)}
                            className="text-telgo-red hover:underline flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Regions
                          </button>
                        </div>

                        <RegionalCountryCountFilter
                          variants={regionalCountryCountVariants}
                          selectedCountryCount={selectedRegionalCountryCount}
                          onSelect={setSelectedRegionalCountryCount}
                        />

                        {regionalBundlesForView.length > 0 && (
                          <>
                            <p className="mb-4 text-center text-sm text-gray-600 md:text-left">
                              {regionalBundlesForView.length}{' '}
                              {regionalBundlesForView.length === 1 ? 'bundle' : 'bundles'} available
                            </p>
                            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 mb-8">
                              {regionalBundlesForView.map((row, index) => (
                                <PopularDestinationCountryCard
                                  key={row.bundle.id}
                                  variant="grid"
                                  gridIndex={index}
                                  flag="🌍"
                                  name={row.title}
                                  pricePerGb={row.pricePerGb}
                                  priceDisplay="perGb"
                                  available={row.available}
                                  onClick={() =>
                                    navigate('/checkout', {
                                      state: {
                                        plan: buildRegionalCheckoutPlan(
                                          row.bundle,
                                          selectedRegionalRegion?.label ?? selectedRegion ?? 'Regional'
                                        ),
                                      },
                                    })
                                  }
                                />
                              ))}
                            </div>
                          </>
                        )}

                        {regionalCountriesForView.length > 0 && (
                          <>
                            <p className="mb-4 text-center text-sm text-gray-600 md:text-left">
                              {regionalCountriesForView.length} countries covered
                            </p>
                            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                              {regionalCountriesForView.map((dest, index) => (
                                <RegionalCoverageCountryCard
                                  key={`${dest.iso}-${index}`}
                                  gridIndex={index}
                                  isoCode={dest.iso}
                                  flag={dest.flag}
                                  name={dest.name}
                                />
                              ))}
                            </div>
                          </>
                        )}

                        {regionalBundlesForView.length === 0 && regionalCountriesForView.length === 0 && (
                          <div className="py-12 text-center">
                            <p className="text-lg text-gray-500">No regional plans found for this region.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Global eSIMs — Nomad global plans from /home-data snapshot */}
            {activeTab === 'global' && (
              <div className="mb-6">
                {homePopularLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-telgo-red" />
                    <p className="mt-4 text-gray-600">Loading global plans…</p>
                  </div>
                ) : homeGlobalPlans.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-lg text-gray-500">No global plans found.</p>
                  </div>
                ) : (
                  <>
                    <p className="mb-4 text-center text-sm text-gray-600 md:text-left">
                      {homeGlobalPlans.length}{' '}
                      {homeGlobalPlans.length === 1 ? 'global plan' : 'global plans'} available
                    </p>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                      {homeGlobalPlans.map((plan, index) => (
                        <PopularDestinationCountryCard
                          key={plan.id}
                          variant="grid"
                          gridIndex={index}
                          flag="🌐"
                          name={plan.name}
                          pricePerGb={
                            plan.available && plan.startingFromUsd != null
                              ? plan.startingFromUsd.toFixed(2)
                              : '0'
                          }
                          priceDisplay="perGb"
                          available={plan.available}
                          onClick={() =>
                            navigate(`/global-esim?bundle=${encodeURIComponent(plan.id)}`)
                          }
                        />
                      ))}
                    </div>
                    <div className="mt-6 text-center">
                      <button
                        type="button"
                        onClick={() => navigate('/shop?tab=global')}
                        className="cursor-pointer rounded-lg border-2 border-telgo-red bg-white px-6 py-2.5 font-semibold text-telgo-red transition-colors hover:bg-telgo-red hover:text-white"
                      >
                        View all in Shop
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Forget About Roaming Section */}
      <section className="pt-4 md:pt-0 pb-28 bg-transparent mt-16 md:-mt-16 mb-12 border-t md:border-0 border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Left Content */}
            <div
              >
              <h2 
                className="mb-8 text-center md:text-left text-2xl md:text-[35px]"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  color: '#181E4B'
                }}
              >
                Why Choose Us
              </h2>
              
              <div className="space-y-6">
                {/* Feature 1 - Highly Trusted */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center">
                    <OptimizedPicture
                      spec={{ ...IMAGE_SPECS.featureIcon, src: '/IMAGES/HighlyTrusted.png' }}
                      alt="Highly Trusted"
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  </div>
                  <div>
                    <h3 
                      className="mb-1"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 700,
                        fontSize: '18px',
                        color: '#5E6282'
                      }}
                    >
                      Highly trusted by users worldwide
                    </h3>
                    <p 
                      className="text-sm md:text-base lg:text-[15px]"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        color: '#5E6282'
                      }}
                    >
                      Highly rated on TrustPilot, App Store, and Google Play.
                    </p>
                  </div>
                </div>
                
                {/* Feature 2 - 24/7 Support */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center">
                    <OptimizedPicture
                      spec={{ ...IMAGE_SPECS.featureIcon, src: '/IMAGES/247.png' }}
                      alt="24/7 Live Support"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 
                      className="mb-1 text-left text-base md:text-lg lg:text-[18px]"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 700,
                        color: '#5E6282'
                      }}
                    >
                      24/7 Live support
                    </h3>
                    <p 
                      className="text-sm md:text-base lg:text-[15px]"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        color: '#5E6282'
                      }}
                    >
                      The TTelGo eSIM customer support team will be ready to help wherever you need, whenever you are.
                    </p>
                  </div>
                </div>
                
                {/* Feature 3 - Reliable */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center">
                    <OptimizedPicture
                      spec={{ ...IMAGE_SPECS.featureIcon, src: '/IMAGES/Reliable.png' }}
                      alt="Reliable and Affordable"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 
                      className="mb-1 text-left text-base md:text-lg lg:text-[18px]"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 700,
                        color: '#5E6282'
                      }}
                    >
                      Reliable and affordable connectivity
                    </h3>
                    <p 
                      className="text-sm md:text-base lg:text-[15px]"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        color: '#5E6282'
                      }}
                    >
                      Stay connected as you travel globally without worrying about expensive roaming fees.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Why Choose Us Image */}
            <div
              className="hidden lg:flex relative w-full items-center justify-center pr-8 lg:pr-16 xl:pr-24"
              style={{ zIndex: 15 }}
            >
              <OptimizedPicture
                spec={IMAGE_SPECS.mobile3d}
                alt="Why Choose Us"
                className="w-full h-auto object-contain max-w-md"
                style={{ maxWidth: '28rem' }}
                loading="lazy"
                deferUntilVisible
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        ref={faqSectionRef}
        className="pt-4 md:pt-0 pb-16 bg-transparent mt-4 md:-mt-24 border-t md:border-0 border-gray-200"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            >
            <h2 
              className="mb-8 text-center text-2xl md:text-[35px]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                color: '#181E4B'
              }}
            >
              TTelGo FAQs
            </h2>
            
            {faqsLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-telgo-red"></div>
                <p className="mt-4 text-gray-600">Loading FAQs...</p>
              </div>
            ) : faqs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No FAQs available at the moment.</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {faqs.slice(0, HOME_FAQ_PREVIEW_LIMIT).map((faq, index) => (
                    <div
                      key={faq.id}
                      className="bg-white rounded-lg overflow-hidden"
                      style={{
                        boxShadow: '0 6px 8px -1px rgba(0, 0, 0, 0.12), 0 3px 5px -1px rgba(0, 0, 0, 0.08), 0 -3px 5px -1px rgba(0, 0, 0, 0.08)',
                        transition: 'box-shadow 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 12px 16px -2px rgba(0, 0, 0, 0.15), 0 6px 8px -2px rgba(0, 0, 0, 0.1), 0 -6px 8px -2px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 6px 8px -1px rgba(0, 0, 0, 0.12), 0 3px 5px -1px rgba(0, 0, 0, 0.08), 0 -3px 5px -1px rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      <button
                        onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                        className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-left font-medium text-gray-900">
                          <span className="text-telgo-red font-semibold mr-2">{index + 1}.</span>
                          {faq.question}
                        </span>
                        <svg
                          className={`w-5 h-5 text-telgo-red transform transition-transform ${
                            openFAQ === faq.id ? 'rotate-45' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                      {openFAQ === faq.id && (
                        <div className="px-6 pb-4 text-gray-600 whitespace-pre-line">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {faqs.length > HOME_FAQ_PREVIEW_LIMIT && (
                  <div className="mt-8 flex justify-center">
                    <Link
                      to="/faq"
                      className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-base font-semibold text-white bg-telgo-red hover:opacity-90 transition-opacity shadow-md"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      View more FAQs
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section
        ref={blogSectionRef}
        className="pt-0 md:pt-0 pb-16 bg-transparent border-t md:border-0 border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            >
            <div className="flex items-end justify-between mb-8">
              <h2 
                className="text-2xl md:text-[35px]"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  color: '#181E4B'
                }}
              >
                From our Blog
              </h2>
              <Link
                to="/blog"
                className="text-telgo-red font-semibold hover:underline"
              >
                View all
              </Link>
            </div>

            {blogLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-telgo-red"></div>
                <p className="mt-4 text-gray-600">Loading blog posts...</p>
              </div>
            ) : blogPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No blog posts available at the moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts.map((post) => {
                  const formattedDate = post.publishedAt
                    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : post.createdAt
                    ? new Date(post.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : ''

                  return (
                    <article
                      key={post.id}
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow"
                    >
                      <Link to={`/blog/${post.slug}`}>
                        <div className="relative h-48 overflow-hidden">
                          <OptimizedPicture
                            src={post.featuredImage || post.image || '/IMAGES/Cities/Rome.jpg'}
                            spec={
                              (post.featuredImage || post.image || '').startsWith('http')
                                ? undefined
                                : specForCityImage(post.featuredImage || post.image || '/IMAGES/Cities/Rome.jpg')
                            }
                            alt={post.title}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.src = '/IMAGES/opt/Cities/Rome-480w.webp'
                            }}
                          />
                          <div className="absolute top-4 left-4">
                            <span className="bg-telgo-red text-white px-3 py-1 rounded-full text-xs font-semibold">
                              {post.category || 'Blog'}
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                            {formattedDate && <span>{formattedDate}</span>}
                            {formattedDate && post.readTime && <span>•</span>}
                            {post.readTime && <span>{post.readTime}</span>}
                          </div>
                          <h3 className="text-lg md:text-xl font-bold mb-3 hover:text-telgo-red transition-colors" style={{ color: '#181E4B' }}>
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                            {post.excerpt || 'Read more about this topic...'}
                          </p>
                          <div className="flex items-center text-telgo-red font-semibold text-sm">
                            Read More
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </Link>
                    </article>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="pt-4 md:pt-8 pb-16 bg-transparent border-t md:border-0 border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.9fr] gap-8 lg:gap-12 items-start">
            {/* Left Section - Label and Heading */}
            <div className="flex flex-col">
              {/* Top: Label and Heading */}
              <div>
                <div className="mb-6">
                  <span className="text-xs text-gray-500 uppercase tracking-[0.2em] font-medium">
                    TESTIMONIALS
                  </span>
                </div>
                <h2 
                  className="leading-tight md:leading-[1.2] text-center md:text-left text-2xl md:text-[35px]"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    color: '#181E4B'
                  }}
                >
                  <span className="block whitespace-nowrap">What People Say</span>
                  <span className="block whitespace-nowrap">About Us.</span>
                </h2>
              </div>
            </div>

            {/* Right Section - Testimonial Cards and Navigation */}
            <div className="relative">
              {/* Testimonial Cards Container */}
              <div className="relative min-h-[380px]">
                {/* Active Testimonial Card */}
                <div
                  key={testimonialIndex}
                  className="bg-white rounded-2xl p-8 relative z-10"
                  style={{
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {/* Profile Picture - Centered at Top, Overlapping Card Border */}
                  <div className="flex justify-center -mt-12 mb-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-xl relative z-20 bg-white">
                      <OptimizedPicture
                        spec={IMAGE_SPECS.testimonialAvatar}
                        alt={testimonials[testimonialIndex].name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const parent = target.parentElement
                          if (parent && !parent.querySelector('span')) {
                            const fallback = document.createElement('span')
                            fallback.className = 'w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 text-white text-2xl font-bold'
                            fallback.textContent = testimonials[testimonialIndex].name.charAt(0).toUpperCase()
                            parent.appendChild(fallback)
                          }
                        }}
                      />
                      {!testimonials[testimonialIndex].image && (
                        <span className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 text-white text-2xl font-bold">
                          {testimonials[testimonialIndex].name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Quote */}
                  <div className="text-center mb-4 pt-2">
                    <p 
                      className="text-base md:text-lg leading-[1.7] max-w-2xl mx-auto"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        color: '#5E6282'
                      }}
                    >
                      &quot;{testimonials[testimonialIndex].quote}&quot;
                    </p>
                  </div>
                  
                  {/* Name */}
                  <div className="text-center mb-0.5">
                    <h3 
                      className="text-lg md:text-xl text-gray-900"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600
                      }}
                    >
                      {testimonials[testimonialIndex].name}
                    </h3>
                  </div>
                  
                  {/* Location */}
                  <div className="text-center">
                    <p 
                      className="text-sm md:text-base"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        color: '#5E6282'
                      }}
                    >
                      {testimonials[testimonialIndex].location}
                    </p>
                  </div>
                </div>
                
                {/* Next Testimonial Card - decorative stack peek (no readable text; parent opacity broke contrast) */}
                {testimonialIndex < testimonials.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="bg-white rounded-2xl p-6 absolute top-[70%] left-2 right-2 z-0 border border-gray-100"
                    style={{
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-20px) scale(0.94)',
                    }}
                  >
                    <div className="flex justify-center -mt-10 mb-3 opacity-60">
                      <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-md bg-gray-100">
                        <OptimizedPicture
                          spec={IMAGE_SPECS.testimonialAvatar}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                          }}
                        />
                      </div>
                    </div>
                    <div className="space-y-2 pt-2">
                      <div className="mx-auto h-3 w-28 rounded-full bg-gray-200" />
                      <div className="mx-auto h-2 w-36 rounded-full bg-gray-100" />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Navigation Arrows - Vertical on Far Right */}
              <div className="absolute right-[-90px] top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-5">
                <button
                  onClick={() => {
                    if (testimonialIndex > 0) {
                      setTestimonialIndex(testimonialIndex - 1)
                    }
                  }}
                  disabled={testimonialIndex === 0}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    testimonialIndex === 0
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-telgo-red text-white hover:bg-red-700 cursor-pointer shadow-lg hover:shadow-xl'
                  }`}
                  aria-label="Previous testimonial"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    if (testimonialIndex < testimonials.length - 1) {
                      setTestimonialIndex(testimonialIndex + 1)
                    }
                  }}
                  disabled={testimonialIndex === testimonials.length - 1}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    testimonialIndex === testimonials.length - 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-telgo-red text-white hover:bg-red-700 cursor-pointer shadow-lg hover:shadow-xl'
                  }`}
                  aria-label="Next testimonial"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {/* Mobile Navigation Arrows - Below Cards */}
              <div className="flex justify-center gap-4 mt-4 xl:hidden">
                <button
                  onClick={() => {
                    if (testimonialIndex > 0) {
                      setTestimonialIndex(testimonialIndex - 1)
                    }
                  }}
                  disabled={testimonialIndex === 0}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    testimonialIndex === 0
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-telgo-red text-white hover:bg-red-700 cursor-pointer'
                  }`}
                  aria-label="Previous testimonial"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    if (testimonialIndex < testimonials.length - 1) {
                      setTestimonialIndex(testimonialIndex + 1)
                    }
                  }}
                  disabled={testimonialIndex === testimonials.length - 1}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    testimonialIndex === testimonials.length - 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-telgo-red text-white hover:bg-red-700 cursor-pointer'
                  }`}
                  aria-label="Next testimonial"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="pt-4 pb-16 bg-transparent relative border-t md:border-0 border-gray-200 -mt-12">
        {/* Dots and Plus Pattern Background */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              radial-gradient(circle, #cbd5e1 1px, transparent 1px),
              radial-gradient(circle, #cbd5e1 1px, transparent 1px),
              linear-gradient(45deg, transparent 48%, #cbd5e1 49%, #cbd5e1 51%, transparent 52%)
            `,
            backgroundSize: '30px 30px, 30px 30px, 20px 20px',
            backgroundPosition: '0 0, 15px 15px, 0 0'
          }}
        ></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div
            className="bg-white rounded-2xl p-8 md:p-12 relative overflow-visible"
            style={{
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Paper Airplane Icon - Positioned in corner */}
            <div className="absolute -top-2 -right-2 w-20 h-20 opacity-50 z-20">
              <OptimizedPicture
                spec={IMAGE_SPECS.paperPlane}
                alt="Paper Airplane"
                className="w-full h-full object-contain"
                style={{
                  filter: 'hue-rotate(240deg) saturate(1.5) brightness(1.1)'
                }}
                loading="lazy"
              />
            </div>
            
            {/* Content with relative positioning */}
            <div className="relative z-10">
              <h2 
                className="mb-6 text-center mx-auto px-2 md:px-4 leading-relaxed text-xl md:text-lg lg:text-[24px]"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  color: '#5E6282'
                }}
              >
                <span className="block">Subscribe to get information, latest news and other</span>
                <span className="block">interesting offers about TTelGo</span>
              </h2>
            
              <form onSubmit={onNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <div className="relative flex-1">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="w-full pl-12 pr-4 py-2.5 rounded-lg bg-white focus:outline-none transition-all placeholder-gray-400 text-gray-900 text-sm focus:ring-2 focus:ring-telgo-red"
                    style={{
                      boxShadow: '0 6px 8px -1px rgba(0, 0, 0, 0.12), 0 3px 5px -1px rgba(0, 0, 0, 0.08), 0 -3px 5px -1px rgba(0, 0, 0, 0.08)',
                      transition: 'box-shadow 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.boxShadow = '0 12px 16px -2px rgba(0, 0, 0, 0.15), 0 6px 8px -2px rgba(0, 0, 0, 0.1), 0 -6px 8px -2px rgba(0, 0, 0, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 8px -1px rgba(0, 0, 0, 0.12), 0 3px 5px -1px rgba(0, 0, 0, 0.08), 0 -3px 5px -1px rgba(0, 0, 0, 0.08)'
                    }}
                    required
                    disabled={newsletterBusy}
                  />
                </div>
                <button
                  type="submit"
                  disabled={newsletterBusy}
                  className="px-6 py-2.5 bg-telgo-red text-white rounded-lg font-semibold hover:bg-red-700 transition-colors duration-200 shadow-md hover:shadow-lg whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {newsletterBusy ? 'Subscribing…' : 'Subscribe'}
                </button>
              </form>
              {(newsletterInfo || newsletterErr) && (
                <p
                  className={`text-center text-sm mt-4 ${newsletterErr ? 'text-red-600' : 'text-green-700'}`}
                  role="status"
                >
                  {newsletterErr ?? newsletterInfo}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
